import { memo, useCallback, useMemo, type FC } from "react";
import type { IndustriesPayload } from "@/components/filters/Industries/types";
import { IndustriesFilter } from "@/components/filters/Industries/IndustriesFilter";
import { useCompanyFilterActions, useCompanyFilterSlice } from "../../hooks";
import {
  defaultIndustrySlice,
  isAllIndustriesEmpty,
  type IndustrySlice,
} from "./slice";

export const IndustryFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("industry");
  const { setSlice } = useCompanyFilterActions();

  const selectedValues = useMemo<IndustriesPayload>(
    () => ({
      industryIds: slice.industryIds,
      sicIds: slice.sicIds,
      naicsIds: slice.naicsIds,
      excludedIds: slice.excludedIds,
      excludedSicIds: slice.excludedSicIds,
      excludedNaicsIds: slice.excludedNaicsIds,
      selectedIndustryType: slice.selectedIndustryType,
    }),
    [slice]
  );

  const handleValuesChange = useCallback(
    (payload: IndustriesPayload) => {
      const normalized: IndustrySlice = {
        industryIds: payload.industryIds ?? [],
        sicIds: payload.sicIds ?? [],
        naicsIds: payload.naicsIds ?? [],
        excludedIds: payload.excludedIds ?? [],
        excludedSicIds: payload.excludedSicIds ?? [],
        excludedNaicsIds: payload.excludedNaicsIds ?? [],
        selectedIndustryType:
          payload.selectedIndustryType ??
          slice.selectedIndustryType ??
          "INDUSTRY",
      };

      if (isAllIndustriesEmpty(normalized)) {
        setSlice("industry", {
          ...defaultIndustrySlice,
          selectedIndustryType: normalized.selectedIndustryType,
        });
        return;
      }

      setSlice("industry", normalized);
    },
    [setSlice, slice.selectedIndustryType]
  );

  return (
    <IndustriesFilter
      id="segments-industries-filter"
      showExcluded
      selectedValues={selectedValues}
      onValuesChange={handleValuesChange}
    />
  );
});

IndustryFilter.displayName = "IndustryFilter";
